import type { TFunction } from "i18next";

import type { Channel, Incident } from "../view-model";

/**
 * Insert spaces between camelCase boundaries so generated names like
 * "CurrentWeight" or "TemperatureController" read as "Current Weight" /
 * "Temperature Controller" in the UI. Acronym-safe (HTTPRequest →
 * HTTP Request).
 */
function humanizeCamelCase(name: string): string {
  return name
    .replaceAll(/([\da-z])([A-Z])/g, "$1 $2")
    .replaceAll(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
}

/**
 * Pull the FU + function/channel name out of the synthetic `incident.name`
 * shape `${fuName}.${functionName}.${type}` produced by mapLADSIncident.
 */
function extractScope(name: string): { fu?: string; channel?: string } {
  const parts = name.split(".");
  if (parts.length < 2) return {};
  // [0]=fu, [1]=function, [2..]=type
  const fu = parts[0] ? humanizeCamelCase(parts[0]) : undefined;
  const channel = parts[1] ? humanizeCamelCase(parts[1]) : undefined;
  return { fu, channel };
}

/**
 * Find the channel an incident belongs to. Primary key is the full browse
 * path (also correct for organized/mirrored functions where the channel id
 * lives under the monitored device but the alarm context is the source
 * path); fallback is the `${deviceId}/${fu}/${func}` id shape derived from
 * `incident.name` — same matching as getIncidentForChannel.
 */
export function findIncidentChannel(
  incident: Incident,
  channels?: Channel[]
): Channel | undefined {
  if (!channels?.length) return undefined;
  const byBrowsePath = incident.browsePath
    ? channels.find((c) => c.browsePath === incident.browsePath)
    : undefined;
  if (byBrowsePath) return byBrowsePath;
  const parts = incident.name.split(".");
  if (parts.length < 2) return undefined;
  const path = `${incident.deviceId}/${parts[0]}/${parts[1]}`;
  return channels.find((c) => c.id === path || c.id.startsWith(path + "/"));
}

/**
 * Render the user-facing alarm message. Format:
 *   `${channel} (${fu}): ${value} ${operator} ${limit}`
 * where operator is `>` for High/HighHigh and `<` for Low/LowLow, and
 * `${limit}` is the AlarmMonitor threshold snapshot captured at fire time.
 * Falls back to the raw OPC-UA message when limit data is missing.
 */
export function formatIncidentMessage(
  incident: Incident,
  t: TFunction,
  channelLabel?: string
): string {
  if (!incident.limitState) return incident.message;

  const { channel: extracted, fu } = extractScope(incident.name);
  // Prefer the resolved channel name (ComponentName → displayName →
  // browseName, resolved by the REST layer) over the humanized
  // browse-path segment — keeps incident texts consistent with
  // Aktuelle Werte / Auswertung / Export.
  const channel = channelLabel || extracted;
  const scope = channel ? (fu ? `${channel} (${fu})` : channel) : fu;

  const value =
    typeof incident.limitValue === "number" &&
    Number.isFinite(incident.limitValue)
      ? incident.limitValue
      : null;

  // Direction + threshold pulled from the captured AlarmMonitor snapshot.
  const isUpper =
    incident.limitState === "High" || incident.limitState === "HighHigh";
  let limit: number | undefined;
  if (isUpper) {
    limit =
      incident.limitState === "HighHigh"
        ? incident.highHighLimit
        : incident.highLimit;
  } else {
    limit =
      incident.limitState === "LowLow"
        ? incident.lowLowLimit
        : incident.lowLimit;
  }
  const operator = isUpper ? ">" : "<";

  // If we have value AND limit → compact `8.64 > 4`. Else fall back to the
  // localized "Wert über/unter Grenzwert"-style text so legacy data without
  // a captured snapshot still renders informatively.
  if (value !== null && typeof limit === "number" && Number.isFinite(limit)) {
    return scope
      ? `${scope}: ${value} ${operator} ${limit}`
      : `${value} ${operator} ${limit}`;
  }

  const stateText = t(`alarm.limitstate.${incident.limitState}`);
  const valueText = value === null ? "" : ` (${value})`;
  return scope
    ? `${scope} — ${stateText}${valueText}`
    : `${stateText}${valueText}`;
}
